// package imports
import React, { useState } from 'react';

// CSS imports
import './EmailLogin.css';

// Component imports
import TextInput from '../../inputs/textInput/TextInput';
import PrimaryButton from '../../buttons/primaryButton/PrimaryButton';
import Alert from '../../alert/Alert';

// Service imports
import { ApiService } from '../../../services/ApiService';

// Icons imports
import { MdError } from 'react-icons/md';
import { IoChevronBack } from 'react-icons/io5';

// Type imports
import { LoginPage } from '../../../types/login/LoginPage';
import { User } from '../../../types/user/User';
import { SignupLoginInput } from '../../../types/authentication/SignupLoginInput';
import { AlertSize } from '../../../types/alert/AlertSize';
import { LoginReturn } from '../../../types/authentication/LoginReturn';
import { LoginResultType } from '../../../types/authentication/LoginResultType';

// Special imports
import { config } from '../../../config';

type Props = {
  switchPage: (x: LoginPage) => void;
  setUser: (x: User | null) => void;
  setIsLoading: (x: boolean) => void;
  setShowLogin: (x: boolean) => void;
};

const EmailLogin: React.FC<Props> = ({
  switchPage,
  setUser,
  setIsLoading,
  setShowLogin,
}: Props) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');

  const [showUnknownError, setShowUnknownError] = useState(false);
  const [showInvalidError, setShowInvalidError] = useState(false);

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    switch (e.target.name) {
      case 'email': {
        setEmail(e.target.value);
        setEmailError('');
        break;
      }
      case 'password': {
        setPassword(e.target.value);
        setPasswordError('');
        break;
      }
    }
  };

  const triggerLogin = () => {
    const isValid = checkInputForError();
    if (isValid) {
      login();
    }
  };

  const login = async () => {
    setIsLoading(true);
    const providedUser: SignupLoginInput = {
      email: email,
      password: password,
    };

    const res: LoginReturn = await ApiService.login(providedUser);

    setIsLoading(false);

    if (res.resultType === LoginResultType.OK && res.user) {
      setUser(res.user);
      setShowLogin(false);
    } else if (res.resultType === LoginResultType.INVALID) {
      setShowInvalidError(true);
    } else {
      setShowUnknownError(true);
    }
  };

  const checkInputForError = () => {
    let isValid = true;

    if (email === '') {
      isValid = false;
      setEmailError('Please enter an email');
    } else if (!/[^@\s]+@[^@\s]+\.[^@\s]+/.test(email)) {
      isValid = false;
      setEmailError('Please enter a valid email');
    }

    if (password === '') {
      isValid = false;
      setPasswordError('Please enter a password');
    }

    return isValid;
  };
  return (
    <div className='EmailLogin'>
      <div className='modal-header'>
        <h1>Log in</h1>
        <div
          className='back-container'
          onClick={() => switchPage(LoginPage.LOGIN)}
        >
          <IoChevronBack className='back-chevron' />
          <span>Back</span>
        </div>
      </div>
      <TextInput
        value={email}
        name='email'
        placeholder='Email'
        onChange={onChange}
        errorMessage={emailError}
        className='full-width'
      />
      <TextInput
        value={password}
        name='password'
        type='password'
        placeholder='Password'
        onChange={onChange}
        errorMessage={passwordError}
        className='full-width password-input'
      />
      {/* <span>Forgot your password?</span> */}
      <PrimaryButton
        label='Log in with email'
        className='full-width log-in-button'
        onClick={triggerLogin}
      />
      <div className='divider'></div>
      <span>
        New to {config.appName}?{' '}
        <span
          className='create-link'
          onClick={() => switchPage(LoginPage.SIGNUP)}
        >
          Create an account
        </span>
      </span>
      <Alert
        showAlert={showUnknownError}
        size={AlertSize.SMALL}
        time={3000}
        setShowAlert={setShowUnknownError}
      >
        <MdError size={20} />
        <span>Something went wrong!</span>
      </Alert>
      <Alert
        showAlert={showInvalidError}
        size={AlertSize.SMALL}
        time={3000}
        setShowAlert={setShowInvalidError}
      >
        <MdError size={20} />
        <span>Invalid email or password!</span>
      </Alert>
    </div>
  );
};

export default EmailLogin;
